import { SlideshowSelectEvent } from "@theme/events";

const SLIDER_SELECTOR = "slideshow-component.pc--crb-slideshow";
const SCROLLER_SELECTOR = "slideshow-slides.pc--crb-carousel__scroller";
const SLIDE_SELECTOR = "slideshow-slide.pc--crb-carousel__slide";
const SLIDE_INNER_SELECTOR = ".pc--crb-carousel__slide-inner";

/** Metafield color from Liquid (per slide). Desktop hover uses this in CSS. */
const ACCENT_VAR = "--crb-disc-accent";

/** Mobile only: copied from accent onto the visible slide so the disc picks it up. */
const DISC_ACTIVE_VAR = "--crb-routine-image-disc-active";

const SCROLL_EDGE_PX = 10;
const SCROLL_SETTLE_MS = 100;

const mobileLayoutMq = window.matchMedia("(max-width: 749px)");

/** @type {WeakMap<HTMLElement, AbortController>} */
const sliderAbortControllers = new WeakMap();

/**
 * @returns {boolean}
 */
function isMobileLayout() {
  return mobileLayoutMq.matches;
}

/**
 * @param {HTMLElement} slide
 * @param {HTMLElement} scroller
 * @returns {number}
 */
function slideOffsetRelativeToScroller(slide, scroller) {
  let pos = 0;
  /** @type {HTMLElement | null} */
  let el = slide;
  while (el && el !== scroller) {
    pos += el.offsetLeft;
    /** @type {Element | null} */
    const op = el.offsetParent;
    el = op instanceof HTMLElement ? op : null;
  }
  return pos;
}

/**
 * Slideshow #sync biases to the scroller leading edge when two slides peek, so `current`
 * and aria-hidden can disagree with the snapped slide at max scroll. Use geometry + end clamp.
 *
 * @param {HTMLElement} scroller
 * @param {Element[]} slides
 * @returns {number}
 */
function getRoutineDiscIndexFromScroll(scroller, slides) {
  const n = slides.length;
  if (n <= 1) return 0;

  const maxScroll = Math.max(0, scroller.scrollWidth - scroller.clientWidth);
  if (maxScroll <= SCROLL_EDGE_PX) return 0;

  const sl = scroller.scrollLeft;

  if (getComputedStyle(scroller).direction !== "rtl") {
    if (sl >= maxScroll - SCROLL_EDGE_PX) return n - 1;
    if (sl <= SCROLL_EDGE_PX) return 0;
  }

  let bestIdx = 0;
  let bestDist = Infinity;
  for (let i = 0; i < n; i++) {
    const slide = slides[i];
    if (!(slide instanceof HTMLElement)) continue;
    const pos = slideOffsetRelativeToScroller(slide, scroller);
    const dist = Math.abs(pos - sl);
    if (dist < bestDist) {
      bestDist = dist;
      bestIdx = i;
    }
  }
  return bestIdx;
}

/**
 * @param {Element} sliderRoot
 */
function clearDiscActiveOnAllInners(sliderRoot) {
  sliderRoot
    .querySelectorAll(`${SLIDE_SELECTOR} ${SLIDE_INNER_SELECTOR}`)
    .forEach((el) => {
      if (el instanceof HTMLElement) el.style.removeProperty(DISC_ACTIVE_VAR);
    });
}

/**
 * @param {Element} sliderRoot
 * @param {Element | null | undefined} slideEl
 */
function applyMobileDiscForSlide(sliderRoot, slideEl) {
  clearDiscActiveOnAllInners(sliderRoot);
  if (!(slideEl instanceof HTMLElement)) return;
  const inner = slideEl.querySelector(SLIDE_INNER_SELECTOR);
  if (!(inner instanceof HTMLElement)) return;
  const accent = getComputedStyle(inner).getPropertyValue(ACCENT_VAR).trim();
  if (accent) inner.style.setProperty(DISC_ACTIVE_VAR, accent);
}

/**
 * @param {Element} sliderRoot
 */
function applyMobileDiscFromScroll(sliderRoot) {
  if (!isMobileLayout()) {
    clearDiscActiveOnAllInners(sliderRoot);
    return;
  }
  const scroller = sliderRoot.querySelector(SCROLLER_SELECTOR);
  if (!(scroller instanceof HTMLElement)) return;
  const slides = [...sliderRoot.querySelectorAll(SLIDE_SELECTOR)];
  if (slides.length === 0) return;
  const idx = getRoutineDiscIndexFromScroll(scroller, slides);
  const slide = slides[idx];
  if (slide) applyMobileDiscForSlide(sliderRoot, slide);
}

/**
 * Slides sit inside .pc--crb-carousel__bordered-track; aligning to the slide rect
 * leaves a gap equal to the track border. Snap scroll to true start for index 0.
 *
 * @param {Element} sliderRoot
 * @param {number} index
 */
function flushRoutineBlockScrollStart(sliderRoot, index) {
  if (index !== 0) return;

  const scroller = sliderRoot.querySelector(SCROLLER_SELECTOR);
  if (!(scroller instanceof HTMLElement)) return;

  const isHorizontal = scroller.scrollWidth > scroller.clientWidth;
  if (!isHorizontal) return;

  const rtl = getComputedStyle(scroller).direction === "rtl";
  if (rtl) return;

  requestAnimationFrame(() => {
    scroller.scrollTo({ left: 0, top: 0, behavior: "instant" });
  });
}

/**
 * @param {HTMLElement} sliderRoot
 * @returns {AbortController}
 */
function getOrCreateSliderController(sliderRoot) {
  if (!sliderAbortControllers.has(sliderRoot)) {
    sliderAbortControllers.set(sliderRoot, new AbortController());
  }
  return /** @type {AbortController} */ (
    sliderAbortControllers.get(sliderRoot)
  );
}

/**
 * @param {Element} sliderRoot
 */
function bindRoutineBlockSlider(sliderRoot) {
  if (!(sliderRoot instanceof HTMLElement)) return;
  if (!sliderRoot.matches(SLIDER_SELECTOR)) return;
  if (sliderRoot.dataset.crbScrollFlushBound === "true") return;
  sliderRoot.dataset.crbScrollFlushBound = "true";

  const { signal } = getOrCreateSliderController(sliderRoot);

  sliderRoot.addEventListener(
    SlideshowSelectEvent.eventName,
    (event) => {
      if (!(event instanceof SlideshowSelectEvent)) return;
      flushRoutineBlockScrollStart(sliderRoot, event.detail.index);
    },
    { signal },
  );
}

/**
 * @param {Element} sliderRoot
 */
function bindRoutineBlockMobileDisc(sliderRoot) {
  if (!(sliderRoot instanceof HTMLElement)) return;
  if (!sliderRoot.matches(SLIDER_SELECTOR)) return;
  if (sliderRoot.dataset.crbMobileDiscBound === "true") return;
  sliderRoot.dataset.crbMobileDiscBound = "true";

  const { signal } = getOrCreateSliderController(sliderRoot);
  const scroller = sliderRoot.querySelector(SCROLLER_SELECTOR);

  /** @param {Event} event */
  const onSelect = (event) => {
    if (!(event instanceof SlideshowSelectEvent)) return;
    if (!isMobileLayout()) return;
    const slides = [...sliderRoot.querySelectorAll(SLIDE_SELECTOR)];
    const slide =
      event.detail.slide instanceof HTMLElement
        ? event.detail.slide
        : slides[event.detail.index];
    if (slide) applyMobileDiscForSlide(sliderRoot, slide);
  };

  sliderRoot.addEventListener(SlideshowSelectEvent.eventName, onSelect, {
    signal,
  });

  if (scroller instanceof HTMLElement) {
    let settleTimer = 0;
    const settle = () => {
      if (!isMobileLayout()) return;
      window.clearTimeout(settleTimer);
      settleTimer = window.setTimeout(() => {
        applyMobileDiscFromScroll(sliderRoot);
      }, SCROLL_SETTLE_MS);
    };

    scroller.addEventListener("scroll", settle, { passive: true, signal });
    scroller.addEventListener(
      "scrollend",
      () => {
        window.clearTimeout(settleTimer);
        if (isMobileLayout()) applyMobileDiscFromScroll(sliderRoot);
      },
      { signal },
    );
  }

  mobileLayoutMq.addEventListener(
    "change",
    () => {
      applyMobileDiscFromScroll(sliderRoot);
    },
    { signal },
  );

  requestAnimationFrame(() => {
    applyMobileDiscFromScroll(sliderRoot);
  });
}

function initAllRoutineBlockSliders() {
  document.querySelectorAll(SLIDER_SELECTOR).forEach((slider) => {
    bindRoutineBlockSlider(slider);
    bindRoutineBlockMobileDisc(slider);
  });
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initAllRoutineBlockSliders);
} else {
  initAllRoutineBlockSliders();
}

/** @param {Event} event */
document.addEventListener("shopify:section:load", (event) => {
  const target = event.target;
  if (!(target instanceof HTMLElement)) return;
  target.querySelectorAll(SLIDER_SELECTOR).forEach((slider) => {
    bindRoutineBlockSlider(slider);
    bindRoutineBlockMobileDisc(slider);
  });
});

/** @param {Event} event */
document.addEventListener("shopify:section:unload", (event) => {
  const target = event.target;
  if (!(target instanceof HTMLElement)) return;
  target.querySelectorAll(SLIDER_SELECTOR).forEach((slider) => {
    if (!(slider instanceof HTMLElement)) return;
    const controller = sliderAbortControllers.get(slider);
    if (controller) {
      controller.abort();
      sliderAbortControllers.delete(slider);
    }
    delete slider.dataset.crbScrollFlushBound;
    delete slider.dataset.crbMobileDiscBound;
  });
});
